import dedent from 'dedent';

/**
 * Shared `<wf-component>`/`<wf-prop>` WHTML grammar doc for the CMS item
 * manifests (create/update/list-cms-collection-items). RichText field values
 * round-trip component instances using this grammar — see
 * `packages/systems/cms/whtml/componentInstanceWhtml.ts` for the emitter/parser.
 *
 * Callers append this to a plain-string base description with
 * `base + '\n\n' + CMS_RICH_TEXT_WHTML_GRAMMAR`; do NOT interpolate it inside
 * another `dedent` template — nesting a pre-dedented string re-runs dedent's
 * common-indent scan over the combined lines and corrupts the indentation.
 */
export const CMS_RICH_TEXT_WHTML_GRAMMAR = dedent`
  # Component Instances in Rich Text Fields

  A RichText field's value may contain embedded Webflow component instances,
  encoded as \`<wf-component>\` elements:

  \`<wf-component data-w-id="..." component-id="..." name="...">...</wf-component>\`

  - \`data-w-id\`: the existing instance's ID. Include it (unchanged) to preserve
    an existing instance, or to update its props. Omit it when inserting a new
    instance.
  - \`component-id\`: which component this is an instance of. Always required —
    the parser resolves the component's definition from it on every write,
    even when updating an existing instance with \`data-w-id\` preserved. Use
    the site's component discovery tool to look up valid component IDs before
    authoring a new instance — request prop schemas (each prop's id, type,
    and name) explicitly, as discovery omits them by default.
  - \`name\`: decorative only — ignored on write, never required.

  Property encoding:
  - Scalar props (text, number, boolean, link, image, etc.): \`prop-<propId>="value"\`
    attributes directly on \`<wf-component>\` — numbers/booleans as their literal
    string form (\`"true"\`/\`"false"\`).
  - Text / RichText props: nested \`<wf-prop name="<propId>" label="<label>" type="text|richtext">...</wf-prop>\`
    child elements — \`label\` is decorative only (ignored on write); \`type\`
    reflects whether the body is plain text (\`"text"\`) or richer HTML (\`"richtext"\`).

  Rules:
  - An unresolvable \`component-id\`, or a component whose definition contains a
    Collection List, rejects the entire write with a 400 error. Fix the
    \`component-id\` or use a different component rather than retrying the same
    payload.
  - A prop that doesn't exist on the component (a typo'd/unknown \`prop-<id>\` or
    \`<wf-prop name="<id>">\`), or a prop value whose type doesn't match the
    field (e.g. text where a number is expected), also rejects the whole write
    with a 400 naming the offending prop and component. Look the component's
    prop ids and types up first (see \`component-id\` above) and correct the
    prop rather than retrying the same payload.
  - If the site doesn't have Components in Rich Text enabled, adding a *new*
    component instance rejects the whole write with a 400 whose message is
    exactly "Adding component instances to Rich Text is disabled" — this is
    unrelated to the \`component-id\` or props you chose, and retrying with a
    different component or prop will not help. Report that the feature isn't
    enabled for this site instead. An existing instance's identity is the
    (\`component-id\`, \`data-w-id\`) pair — editing props while keeping both
    unchanged is unaffected and still works, but changing \`component-id\` on
    an existing \`data-w-id\` still counts as a new instance and 400s the same
    way.
  - A component may contain a Slot and still be inserted into Rich Text, as long
    as the Slot is left unpopulated: encode that by omitting any \`<wf-slot>\`
    element and any slot-content \`prop-*\`/\`<wf-prop>\` override entirely. Any
    \`<wf-slot>\` tag or slot-content override — even an empty one — counts as
    populating the Slot and rejects the entire write with a 400 whose message
    contains "Component slots can't be populated in Rich Text." This is not
    fixed by picking a different component or prop — remove the Slot content and
    retry.
  - Never hand-construct a component's internal HTML — only \`data-w-id\`,
    \`component-id\`, \`name\`, and \`prop-*\`/\`<wf-prop>\` overrides are respected.
  - When reading RichText field values back (e.g. before an update), preserve
    any \`<wf-component>\`/\`<wf-prop>\` markup as-is unless intentionally changing
    that instance's props.
`;

/**
 * One-line summary of the grammar for read-only tools (e.g.
 * list-cms-collection-items) that only need to warn callers not to mangle
 * component markup they read back. Kept in this module so it stays in sync
 * with the full grammar above rather than being hand-duplicated per manifest.
 */
export const CMS_RICH_TEXT_WHTML_GRAMMAR_SUMMARY = dedent`
  RichText field values may contain embedded Webflow component instances encoded as \`<wf-component data-w-id="..." component-id="...">\` elements, with scalar props as \`prop-<propId>="value"\` attributes and text/richtext props as nested \`<wf-prop type="text|richtext">...</wf-prop>\` elements. Preserve this markup as-is when passing a field value on to update-cms-collection-items unless you intend to change that instance's props.
`;
